// RecordProvider — the React context boundary around a form-view's
// state machine (WS 4.1). Hosts a `useFormState` instance and makes it
// available to every descendant via `useFormStateContext()`.
//
// Lifetime: the host is expected to remount the provider on URL change
// (e.g. `<RecordProvider key={recordRef ?? "new"} …>`) so the route-
// scoped state convention from the legacy code is preserved without
// per-key effects scattered through children. With the key change React
// fully unmounts + remounts the provider tree — no race-ref bugs.

import { createContext, useContext, useMemo } from "react";
import type { ReactNode } from "react";
import type { FieldDef, FormElement, Json, RecordData } from "../../core";
import { useFormState, type FormState } from "./useFormState";

interface RecordProviderProps {
    /** Server-loaded record. `null` ⇒ new-record draft. */
    record: RecordData | null | undefined;
    /** Whether this is a brand-new record (no existing recordRef). */
    isNew: boolean;
    /** Field-defs from the action's field_defs payload. */
    fieldDefs: FieldDef[];
    /** Field names declared in the form view DSL. `null` ⇒ no filter. */
    viewFieldNames?: string[] | null;
    /** Existing record's uuid (undefined on new-record draft). */
    recordRef?: string;
    /** Async save handlers — wired by the consumer to the actual
        runtime services (ActionService.createRecord / updateRecord) or
        to a relational bufferer. The optional `draft` second argument
        carries the pre-extraction shape (M2O `{id, display_name}` etc.)
        for callers that need it for optimistic local rendering;
        consumers persisting to the backend can ignore it. */
    onCreate: (values: Record<string, Json>, draft?: RecordData) => Promise<RecordData>;
    onUpdate: (recordRef: string, values: Record<string, Json>, draft?: RecordData) => Promise<RecordData>;
    /** Optional initial value for `isEditing`. When unset, defaults to
        `isNew`. Pass `true` to open an existing record already in edit
        mode (used by RelationListView's popup when the parent form is
        editing). */
    initialIsEditing?: boolean;
    /** Form sheet elements — fed into useFormState so required-validation
        can skip fields hidden by an `invisible="..."` predicate (their
        value is either backend-managed or not user-fillable in the
        current draft state). */
    formElements?: FormElement[];
    /** Extra field names to treat as invisible for required-validation,
        on TOP of whatever the form's `invisible="..."` predicates yield.
        Used by RelationListView's popup to auto-skip back-reference FKs
        whose target equals the parent's model (the backend nested-tuple
        handler injects them, so the user never needs to declare them
        invisible="1" by hand). */
    extraInvisibleFields?: string[];
    children: ReactNode;
}

const FormStateCtx = createContext<FormState | null>(null);

export function RecordProvider({
    record,
    isNew,
    fieldDefs,
    viewFieldNames,
    recordRef,
    onCreate,
    onUpdate,
    initialIsEditing,
    formElements,
    extraInvisibleFields,
    children,
}: RecordProviderProps) {
    const state = useFormState({
        initialRecord: record,
        isNew,
        fieldDefs,
        viewFieldNames,
        recordRef,
        onCreate,
        onUpdate,
        initialIsEditing,
        formElements,
        extraInvisibleFields,
    });
    // Memoise nothing extra here — the hook's individual fields already
    // change by reference only when they change semantically, and React
    // 19's auto-memo handles render skipping fine. Wrapping `state` in
    // useMemo wouldn't add stability since `state` itself is the same
    // object reference per render from the hook.
    const value = useMemo(() => state, [state]);
    return <FormStateCtx.Provider value={value}>{children}</FormStateCtx.Provider>;
}

/**
 * Read the form-state from inside the RecordProvider subtree. Throws if
 * called outside a provider — saves consumers from silent staleness.
 */
export function useFormStateContext(): FormState {
    const ctx = useContext(FormStateCtx);
    if (ctx === null) {
        throw new Error(
            "useFormStateContext: not inside a <RecordProvider>. " +
            "Wrap the form view with <RecordProvider> before reading form state.",
        );
    }
    return ctx;
}

/** Same as `useFormStateContext` but returns `null` outside the provider
    instead of throwing — useful for components that render in both
    form-mode and list-mode contexts and want to gracefully no-op. */
export function useOptionalFormStateContext(): FormState | null {
    return useContext(FormStateCtx);
}
